import React, { useEffect, useMemo, useRef, useState } from "react";

import { Close as CloseIcon } from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Fade, IconButton, Modal } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import useDialogState from "@/hooks/useDialogState";

const SimpleLightBox: React.FC = ({ children }) => {
  const { classes, cx } = useStyles();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageTransitioning, setImageTransitioning] = useState(false);
  const [isImageSet, setIsImageSet] = useState(false);
  const [images, setImages] = useState<HTMLImageElement[]>([]);
  const lightbox = useDialogState();

  const _imagesContainer = useRef<HTMLDivElement>(null);

  const _src = images[currentIndex]?.src ?? undefined;

  const _displayControls = images.length > 1;

  const _next = () => {
    setCurrentIndex((value) => {
      let nextIndex = value + 1;
      if (nextIndex >= images.length) {
        nextIndex = 0;
      }
      return nextIndex;
    });
  };

  const _prev = () => {
    setCurrentIndex((value) => {
      let prevIndex = value - 1;
      if (prevIndex < 0) {
        prevIndex = images.length - 1;
      }
      return prevIndex;
    });
  };

  const _onNext = () => {
    if (!imageTransitioning) {
      setImageTransitioning(true);
      _next();
      _transition(setImageTransitioning);
    }
  };

  const _onPrev = () => {
    if (!imageTransitioning) {
      setImageTransitioning(true);
      _prev();
      _transition(setImageTransitioning);
    }
  };

  const _transition = (
    setState: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    requestAnimationFrame(() => setTimeout(() => setState(false), 320));
  };

  const _renderFullImage = useMemo(() => {
    return (
      <Fade
        in={!imageTransitioning}
        timeout={{ enter: 320, exit: 0 }}
        unmountOnExit
      >
        <img className={classes.image} src={_src} />
      </Fade>
    );
  }, [_src, imageTransitioning]);

  useEffect(() => {
    const findImages = () => {
      if (_imagesContainer.current) {
        const imgElements =
          _imagesContainer.current.getElementsByTagName("img");
        const imagesArray = Array.from(imgElements);
        if (!isImageSet) {
          imagesArray.forEach((img) => {
            const image = new Image();
            image.src = img.src;
            image.alt = img.alt;
            image.id = img.id;
            image.width = img.width;
            image.height = img.height;

            img.addEventListener("click", lightbox.show);
            setImages((prev) => [...prev, img]);
            setIsImageSet(true);
          });
        }
      }
    };

    findImages();
  }, [children]);

  if (children == null) return null;

  return (
    <>
      <div ref={_imagesContainer}>{children}</div>
      <Modal open={lightbox.shown} className={classes.root}>
        <div className={classes.container}>
          <IconButton
            className={classes.closeButton}
            size="small"
            onClick={lightbox.hide}
          >
            <CloseIcon />
          </IconButton>

          {_displayControls && (
            <div className={classes.previous}>
              <IconButton
                className={classes.controlButton}
                size="small"
                onClick={_onPrev}
              >
                <ChevronLeftIcon />
              </IconButton>
            </div>
          )}

          <div className={classes.imageContainer}>{_renderFullImage}</div>

          {_displayControls && (
            <div className={classes.next}>
              <IconButton
                className={classes.controlButton}
                size="small"
                onClick={_onNext}
              >
                <ChevronRightIcon />
              </IconButton>
            </div>
          )}

          {_displayControls && (
            <div className={classes.indicators}>
              {images.map((photo) => (
                <div
                  key={photo.id}
                  className={cx(
                    classes.indicator,
                    photo.src === _src && classes.indicatorActive
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

const useStyles = makeStyles()({
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  container: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: "100%",
  },
  imageContainer: {
    height: "64%",
    width: "64%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  previous: {
    position: "absolute",
    left: 15,
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
  },
  next: {
    position: "absolute",
    right: 15,
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
  },
  controlButton: {
    color: "white",
    borderRadius: 0,
    backgroundColor: "#000",
    pointerEvents: "auto",
    "&:hover": {
      backgroundColor: "rgba(255,255,255, 0.5)",
    },
  },
  closeButton: {
    color: "white",
    backgroundColor: "#000",
    position: "absolute",
    borderRadius: 0,
    right: 0,
    top: 0,
    "&:hover": {
      backgroundColor: "rgba(255,255,255, 0.5)",
    },
  },
  indicators: {
    position: "absolute",
    bottom: 12,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    gap: 3,
  },
  indicator: {
    width: 8,
    height: 8,
    borderRadius: 9999,
    backgroundColor: "#fff",
    opacity: 0.5,
  },
  indicatorActive: {
    opacity: 1.0,
  },
});

export default SimpleLightBox;
